import { Card, CardContent, CardHeader, CardTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { AccountHolderCell } from '@/components/data-table/cells';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { StatusChip } from '@/components/ui/status-chip';
import { CHIP_CLASS, formatEurString, fundsStatusVariant, userStatusVariant } from '@/features/fee-runs/fee-run-helpers';
import { FeeRunUserDialog } from '@/features/fee-runs/fee-run-user-dialog';
import { useFeeRunUsers } from '@/hooks/queries/fees/use-fee-runs';
import type { FeeRunUser, FeeRunUserStatus } from '@/services/api/fee-runs';
import { formatSnakeCase } from '@/utils/string';

const PAGE_SIZE = 50;
const ALL_STATUSES = 'ALL';

const STATUS_OPTIONS: ReadonlyArray<{ value: FeeRunUserStatus | typeof ALL_STATUSES; label: string }> = [
  { value: ALL_STATUSES, label: 'All' },
  { value: 'PLANNED', label: 'Planned' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'DONE', label: 'Done' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'FAILED', label: 'Failed' },
];

/** The run's account holders, one row each, paged from the API; a row opens the charge breakdown. */
export const FeeRunUsersTable = ({ runId, isRunActive }: { runId: string; isRunActive: boolean }) => {
  const [status, setStatus] = useState<FeeRunUserStatus | typeof ALL_STATUSES>(ALL_STATUSES);
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const [selected, setSelected] = useState<FeeRunUser | null>(null);
  const cursor = pagination.cursors[pagination.currentPage];

  const query = useFeeRunUsers(runId, { status: status === ALL_STATUSES ? undefined : status, cursor, limit: PAGE_SIZE }, isRunActive);
  const { data, isPending, isFetching, isError, error, refetch } = query;
  const nextCursor = data?.cursor;

  useEffect(() => {
    if (nextCursor) setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [nextCursor]);

  const handleStatusChange = (value: string) => {
    setStatus(value as FeeRunUserStatus | typeof ALL_STATUSES);
    setPagination(INITIAL_PAGINATION);
  };

  const columns = useMemo(
    (): ColumnDef<FeeRunUser, unknown>[] => [
      { accessorKey: 'pid', header: 'Account holder', cell: ({ row }) => <AccountHolderCell value={row.original.pid} /> },
      { accessorKey: 'entity', header: 'Entity' },
      { accessorKey: 'feeType', header: 'Fee', cell: ({ row }) => <span className="capitalize">{row.original.feeType}</span> },
      { accessorKey: 'userStatus', header: 'Account', cell: ({ row }) => formatSnakeCase(row.original.userStatus) },
      {
        accessorKey: 'totalEurBalance',
        header: 'Balance (EUR)',
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatEurString(row.original.totalEurBalance)}</span>,
      },
      { accessorKey: 'finalFeeEur', header: 'Fee (EUR)', cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatEurString(row.original.finalFeeEur)}</span> },
      { accessorKey: 'adjustment', header: 'Adjustment', cell: ({ row }) => <span className="text-xs">{formatSnakeCase(row.original.adjustment)}</span> },
      {
        id: 'charges',
        header: 'Charges',
        accessorFn: (row) => Object.keys(row.charges).length,
        cell: ({ row }) => <span className="tabular-nums">{Object.keys(row.original.charges).length}</span>,
      },
      {
        id: 'outcome',
        header: 'Outcome',
        accessorFn: (row) => row.status,
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-1">
            <StatusChip className={CHIP_CLASS} variant={userStatusVariant(row.original.status)}>
              {formatSnakeCase(row.original.status)}
            </StatusChip>
            {row.original.status !== 'PLANNED' ? (
              <span className="text-xs tabular-nums text-muted-foreground">
                {row.original.chargedCount}✓ {row.original.skippedCount}↷ {row.original.failedCount}✕
                {row.original.unknownCount > 0 ? <span className="text-amber-500"> {row.original.unknownCount}?</span> : null}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        accessorKey: 'fundsStatus',
        header: 'Ledger',
        cell: ({ row }) => (
          <StatusChip className={CHIP_CLASS} variant={fundsStatusVariant(row.original.fundsStatus)}>
            {formatSnakeCase(row.original.fundsStatus)}
          </StatusChip>
        ),
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2">
          Account holders
          {isFetching && !isPending ? <Spinner className="size-4" /> : null}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Label htmlFor="fee-run-user-status">Outcome</Label>
          <Select onValueChange={handleStatusChange} value={status}>
            <SelectTrigger className="w-40" id="fee-run-user-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isError ? <QueryErrorBanner error={error} onRetry={() => refetch()} title="Failed to fetch the run's account holders" /> : null}
        <DataTable
          columns={columns}
          data={data?.users}
          emptyMessage={status === ALL_STATUSES ? 'No account holders are charged in this run.' : 'No account holders with that outcome.'}
          isLoading={isPending}
          onRowClick={setSelected}
          surface={false}
        />
        {pagination.totalPages > 1 ? (
          <div className="flex justify-end">
            <CursorPagination
              ariaLabel="Account holder pages"
              currentPage={pagination.currentPage}
              onPageChange={(page) => setPagination((previous) => ({ ...previous, currentPage: page }))}
              totalPages={pagination.totalPages}
            />
          </div>
        ) : null}
        <FeeRunUserDialog onClose={() => setSelected(null)} user={selected} />
      </CardContent>
    </Card>
  );
};
FeeRunUsersTable.displayName = 'FeeRunUsersTable';
